import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { EditLicensingOptionsMenuView } from '../view/EditLicensingOptionsMenuView';
import { templateEditLicensingOptionsMenu } from '../view/template';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { EditLicensingOptionsMenuWebComponent } from '../component/EditLicensingOptionsMenuWebComponent';
import { CLOSE_EDIT_LICENSING_OPTIONS_MENU_EVENT } from '../EditLicensingOptionsMenu.Constants';
import { EditLicensingOptionsViewData } from '../types/EditLicensingOptionsViewData';
import _ from 'lodash';
import { SubscriptionType } from '../../../../license/types/SubscriptionType';
import { LicenseChangeMessageType, LicensingTabEvents } from '../../licensingTab/LicensingEnums';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { CompanyData, CompanyFeatures, License } from '../../../types/company-data';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { LicenseChangeEdrPopUp } from '../../licenseChangePopUp/LicenseChangeEdrPopUp';
import { LicenseChangeCompanionPopUp } from '../../licenseChangePopUp/LicenseChangeCompanionPopUp';
import { RemoveProductConfirmationWindow } from '../../removeProductConfirmationWindow/RemoveProductConfirmationWindow';
import { FormGroupValue } from '../../../../ui/gzForm/formGroup/types/form-group-value';
import { EditLicensingOptionsMenuRepository } from '../repository/EditLicensingOptionsMenuRepository';
import { LicenseMonthlyAvailableBundlesForAffectedDescendantsFormWebComponent } from '../../../../license/common/availableBundlesForAffectedDescendantsForm';
import {
  WEB_COMPONENT_GZ_LINK,
  WEB_COMPONENT_LICENSE_MONTHLY_AVAILABLE_BUNDLES_FOR_AFFECTED_DESCENDANTS_FORM,
} from '../../../../webcomponents.constants';
import { FeatureFlag, FeaturesForBundlesDelegate } from '@nirvana/shared/constants';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { AvailableBundlesForAffectedDescendants } from '../../../../license/types/Bundles';

export class EditLicensingOptionsMenuController extends BaseWebComponentController {
  public component: EditLicensingOptionsMenuWebComponent;
  public view: EditLicensingOptionsMenuView;
  public viewConstructor: typeof EditLicensingOptionsMenuView;
  public dataRepository: EditLicensingOptionsMenuRepository;
  public dataRepositoryConstructor: typeof EditLicensingOptionsMenuRepository;

  private bundleFlagToBeAssignedOnUnderneathAffectedCompanies: FeatureFlag;

  constructor(component: EditLicensingOptionsMenuWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: EditLicensingOptionsMenuView,
      viewTemplate: templateEditLicensingOptionsMenu,
      dataRepositoryConstructor: EditLicensingOptionsMenuRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.preSave,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeMenu,
      },
    ];
  }

  async getDataForView(): Promise<EditLicensingOptionsViewData> {
    return {
      company: this.component.company,
      context: this.component.context,
      companyLicenseInformation: this.component.companyLicenseInformation,
    };
  }

  private async preSave() {
    const company = this.component.company;

    if (!company) {
      return;
    }

    const formValue = this.view.dom.$licenseMainForm.value;
    const currentSubscriptionType = CompanyData.determineCompanySubscription(company);
    const selectedSubscriptionType = _.get(formValue, ['subscription']) as SubscriptionType;
    const newFeatures = _.get(formValue, ['specifics', 'features'], {});

    let showProtectionModelChangeWarning = false;
    let availableBundlesForAffectedDescendants;

    let confirmCallback = async () => {
      await this.save();
    };

    const noAffectedDescendants = await this.countAffectedDescendants(selectedSubscriptionType, formValue, company);
    if (noAffectedDescendants > 0) {
      showProtectionModelChangeWarning = true;
      availableBundlesForAffectedDescendants =
        await this.dependencies.api.clients.Company.GetAvailableProtectionModelsForAffectedDescendants(
          newFeatures
        ).toPromise();
      // change confirmCallback in order to display also the protection model warning
      confirmCallback = this.showProtectionModelChangeWarning.bind(
        this,
        noAffectedDescendants,
        availableBundlesForAffectedDescendants,
        company._id,
        newFeatures
      );
    }

    // license has been changed from Monthly License Trial to Basic / Monthly Subscription -> show warning
    if (
      currentSubscriptionType === SubscriptionType.MonthlyLicenseTrial &&
      (selectedSubscriptionType === SubscriptionType.Inherited ||
        selectedSubscriptionType === SubscriptionType.BasicMonthly)
    ) {
      this.showLicenseMspTrialToInheritWarning(confirmCallback);
      return;
    }

    if (
      selectedSubscriptionType === SubscriptionType.Inherited ||
      selectedSubscriptionType === SubscriptionType.InheritedMspTrial
    ) {
      const currentAssignedProduct = MonthlyProductType.getAssignedProduct(company.specifics.features);
      const selectedProduct = MonthlyProductType.getAssignedProduct(_.get(formValue, ['specifics', 'features'], {}));

      if (currentAssignedProduct !== selectedProduct) {
        this.showAssignedProductTypeChangeWarning(confirmCallback);
        return;
      }
    }

    if (showProtectionModelChangeWarning) {
      this.showProtectionModelChangeWarning(
        noAffectedDescendants,
        availableBundlesForAffectedDescendants,
        company._id,
        newFeatures
      );
      return;
    }

    const isPrePaidForm = selectedSubscriptionType === SubscriptionType.Licensed;
    const licenseKey = _.get(formValue, ['specifics', 'licenses', 0, 'key'], '');
    if (isPrePaidForm && licenseKey.length === 0) {
      // prevents accidental product remove
      this.showRemoveProductDialogWindow();
      return;
    }

    await this.save();
  }

  private async countAffectedDescendants(
    selectedSubscriptionType: SubscriptionType,
    formValue: FormGroupValue,
    company: CompanyData
  ): Promise<number> {
    if (selectedSubscriptionType !== SubscriptionType.Inherited) {
      return 0;
    }

    const newFeatures = _.get(formValue, ['specifics', 'features'], {});
    const oldFeatures = company.specifics.features;
    if (!this.userHasRemovedDelegatedProtectionModelForCompany(oldFeatures, newFeatures)) {
      return 0;
    }

    return await this.dependencies.api.clients.Company.GetAffectedDescendantsThroughTheRemovalOfProtectionModels(
      company._id,
      newFeatures
    ).toPromise();
  }

  private userHasRemovedDelegatedProtectionModelForCompany(
    oldFeatures: CompanyFeatures,
    newFeatures: CompanyFeatures
  ): boolean {
    for (const feature in oldFeatures) {
      if (
        FeaturesForBundlesDelegate.includes(feature as FeatureFlag) &&
        oldFeatures[feature] &&
        !newFeatures[feature]
      ) {
        return true;
      }
    }
    return false;
  }

  private async save() {
    const company = this.component.company;
    if (!company) {
      throw new Error('Edit licensing options save: company not found');
    }

    // show spinner
    this.view.dom.$saveButton?.setLoadingState(true);

    // test if prePaid form is active and selected to call required RPC
    const isPrePaidForm =
      _.get(this.view.dom.$licenseMainForm.value, ['subscription'], null) === SubscriptionType.Licensed;

    try {
      if (isPrePaidForm) {
        await this.addLicenseProduct(this.view.dom.$licenseMainForm.value, company._id);
      } else {
        await this.updateLicensingInformation(company._id);
      }

      this.handleFormSavedSuccessfully(company);
      this.showSuccessToast();
      this.closeMenu();
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      this.showErrorToast(error);
    } finally {
      // remove the spinner
      this.view.dom.$saveButton?.setLoadingState(false);
    }
  }

  private async updateLicensingInformation(companyId: string): Promise<void> {
    // if Save is clicked in Edit licensing options, only the fields that are present in this form should be changed
    const dbRecord = await this.dataRepository.getManagedCompanyById(companyId);
    const formValue = this.view.dom.$licenseMainForm.value;
    formValue.bundleFlagToBeAssignedOnUnderneathAffectedCompanies =
      this.bundleFlagToBeAssignedOnUnderneathAffectedCompanies;
    const updatedCompanyRecord = _.merge({}, dbRecord, formValue);
    updatedCompanyRecord.specifics.licenses = _.get(formValue, 'specifics.licenses', []) as License[];

    await this.dependencies.api.clients.Company.SaveRecord(updatedCompanyRecord).toPromise();
  }

  private closeMenu() {
    // any code added here will not be executed when the 'x' button is pressed, from the side menu

    this.component.dispatchEvent(
      new CustomEvent(CLOSE_EDIT_LICENSING_OPTIONS_MENU_EVENT, {
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Licensing options have updated successfully.'),
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(error: Error) {
    let errorMessage;

    if (error['exceptionType'] === 'Exceptions\\NoInternetConnection') {
      errorMessage = this.dependencies.translateFn(
        'The licensing server is temporarily unavailable. Please try again later.'
      );
    } else {
      errorMessage = error?.message || this.translator.getGenericErrorMessage();
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  private handleFormSavedSuccessfully(company: CompanyData) {
    if (this.component.context !== CompanyActionContext.Edit) {
      return;
    }

    const formValue = this.view.dom.$licenseMainForm.value;
    const currentSubscriptionType = company ? CompanyData.determineCompanySubscription(company) : null;
    const selectedSubscriptionType = _.get(formValue, ['subscription']);
    const shouldWarningBeTriggered =
      currentSubscriptionType !== selectedSubscriptionType || selectedSubscriptionType === SubscriptionType.Licensed;

    this.view.dom.$licenseMainForm.resetPrepaidSubform();

    this.component.dispatchEvent(
      new CustomEvent(LicensingTabEvents.LicenseOptionsChange, {
        detail: {
          shouldWarningBeTriggered: shouldWarningBeTriggered,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  /**
   * Displays a warning regarding the subscription change from Monthly License Trial to Basic / Monthly Subscription
   */
  private showLicenseMspTrialToInheritWarning(confirmCallback?: null | EventListenerOrEventListenerObject): void {
    const dialogBodyHtml = `
      <p>
      ${this.dependencies.translateFn(
        'The target company and its customers with monthly subscription will use seats from your pool of licenses. Usage fees will apply for the selected services. Continue?'
      )}
      </p>
    `;

    const dialogFooterHtml = `
        <gz-button id='confirm' type='outline' color='negative' class='mr-2'>
         ${this.dependencies.translateFn('Yes')}
        </gz-button>
        <gz-button id='cancel' type='outline' color='secondary'>
          ${this.dependencies.translateFn('Cancel')}
        </gz-button>`;

    const dialogTitle = this.dependencies.translateFn('Are you sure?');

    GzDialogWebComponent.computeCustomDialog(dialogBodyHtml, dialogFooterHtml, dialogTitle, null, confirmCallback);
  }

  private showAssignedProductTypeChangeWarning(confirmCallback?: null | EventListenerOrEventListenerObject): void {
    const dialogBodyHtml = `
      <p>
      ${this.dependencies.translateFn(
        'The selected product type requires installation of its features set. You need to run a Reconfigure agent task on all managed endpoints to deploy the new features.<br>Existing product expires in 7 days, after which unsupported modules will cease to function.'
      )}
      </p>
    `;

    const dialogFooterHtml = `
        <gz-button id='confirm' type='outline' color='negative' class='mr-2'>
         ${this.dependencies.translateFn('Continue')}
        </gz-button>
        <gz-button id='cancel' type='outline' color='secondary'>
          ${this.dependencies.translateFn('Cancel')}
        </gz-button>`;

    const dialogTitle = this.dependencies.translateFn('Product type confirmation');

    GzDialogWebComponent.computeCustomDialog(dialogBodyHtml, dialogFooterHtml, dialogTitle, null, confirmCallback);
  }

  private showProtectionModelChangeWarning(
    affectedDescendantsCount: number,
    availableBundlesForAffectedDescendants: AvailableBundlesForAffectedDescendants,
    companyId: string,
    companyFeatures: CompanyFeatures
  ): void {
    const dialogBodyHtml = `
    <p style="line-height: 20px; margin-bottom: 20px; margin-top: 4px">
      ${this.dependencies.translateFn(
        'Once removed from reselling, all associated features will be removed from child companies.'
      )}
      </p>
      <p style="line-height: 20px; margin-bottom: 20px">
      ${this.dependencies.translateFn(
        ' You are about to disable features used by {affectedDescendantsCount} companies. For the list of affected companies, download {csv_file}',
        {
          affectedDescendantsCount: '<b>' + affectedDescendantsCount + '</b>',
          csv_file: `<gz-link>${this.dependencies.translateFn('this CSV file')}</gz-link>`,
        }
      )}
      </p>
      <p style="line-height: 20px; margin-bottom: -5px">
       ${this.dependencies.translateFn(
         'Select one of the products below to automatically assign it to all affected companies:'
       )}
      </p>

      <gz-license-monthly-available-bundles-for-affected-descendants-form></gz-license-monthly-available-bundles-for-affected-descendants-form>

      <p style="line-height: 20px; margin-bottom: 1px; margin-top: 15px">
      ${this.dependencies.translateFn(
        'Alternatively, you can cancel and manually assign a different protection model for each company from the {companies} page.',
        {
          companies: '<b>' + this.dependencies.translateFn('Companies') + '</b>',
        }
      )}
      </p>
    `;

    const dialogFooterHtml = `
        <gz-button id='confirm' type='full' color='primary' class='mr-2' disabled>
         ${this.dependencies.translateFn('Save and assign')}
        </gz-button>
        <gz-button id='cancel' type='outline' color='secondary'>
          ${this.dependencies.translateFn('Cancel')}
        </gz-button>`;

    const dialogTitle = this.dependencies.translateFn('Remove protection models?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      dialogBodyHtml,
      dialogFooterHtml,
      dialogTitle,
      null,
      async () => {
        const leftOverBundlesToDelegateForm = dialog.bodyContent.querySelector(
          WEB_COMPONENT_LICENSE_MONTHLY_AVAILABLE_BUNDLES_FOR_AFFECTED_DESCENDANTS_FORM
        ) as LicenseMonthlyAvailableBundlesForAffectedDescendantsFormWebComponent;
        this.bundleFlagToBeAssignedOnUnderneathAffectedCompanies = leftOverBundlesToDelegateForm.value;
        await this.save();
      }
    );
    const leftOverBundlesComponent = dialog.bodyContent.querySelector(
      WEB_COMPONENT_LICENSE_MONTHLY_AVAILABLE_BUNDLES_FOR_AFFECTED_DESCENDANTS_FORM
    ) as LicenseMonthlyAvailableBundlesForAffectedDescendantsFormWebComponent;
    leftOverBundlesComponent.availableBundles = availableBundlesForAffectedDescendants;
    leftOverBundlesComponent.addEventListener('connected', () => {
      leftOverBundlesComponent.getFormGroup().valid$.subscribe({
        next: valid => {
          (dialog?.footerContent?.querySelector('#confirm') as GzButtonWebComponent).disabled = !valid;
        },
      });
    });

    const csvLink = dialog.bodyContent.querySelector(WEB_COMPONENT_GZ_LINK) as GzLinkWebComponent;
    csvLink.onclick = () => {
      this.dependencies.api.clients.Company.DownloadCSVFileWithAffectedUnderneathCompanies(companyId, companyFeatures)
        .toPromise()
        .then()
        .catch();
    };
  }

  private createAndShowPopUpAfterLicenseChange(type: LicenseChangeMessageType) {
    switch (type) {
      case LicenseChangeMessageType.Edr:
        this.createAndShowEdrPopUpAfterLicenseChange();
        break;
      case LicenseChangeMessageType.Companion:
        this.createAndShowCompanionPopUpAfterLicenseChange();
        break;
      default:
        break;
    }
  }

  private createAndShowEdrPopUpAfterLicenseChange() {
    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeEdrPopUp(
        this.component.company?._id
      ).toPromise();
    };

    LicenseChangeEdrPopUp.showLicenseChangeEdrPopUp(this.dependencies.translateFn, null, confirmCallback);
  }

  private createAndShowCompanionPopUpAfterLicenseChange() {
    const shouldButtonsBeVisible = _.get(this.component.company, ['specifics', 'rights', 'manageNetworks'], false);

    const confirmCallback = async () => {
      await this.dependencies.api.clients.Licenses.ReconfigureAfterLicenseChangeCompanionPopUp().toPromise();
    };

    LicenseChangeCompanionPopUp.showLicenseChangeCompanionPopUp(
      this.dependencies.translateFn,
      shouldButtonsBeVisible,
      null,
      confirmCallback
    );
  }

  private showRemoveProductDialogWindow() {
    RemoveProductConfirmationWindow.showRemoveProductConfirmationWindow(
      this.dependencies.translateFn,
      null,
      async () => {
        await this.save();
      }
    );
  }

  private async addLicenseProduct(formValue: FormGroupValue, companyId: string) {
    const response = await this.dependencies.api.clients.Company.AddLicenseProduct(companyId, {
      key: _.get(formValue, ['specifics', 'licenses', 0, 'key'], null),
      mdrContactInformation: _.get(formValue, ['specifics', 'mdrContactInformation'], null),
    }).toPromise();

    if (_.isNumber(response.messageType)) {
      this.createAndShowPopUpAfterLicenseChange(response.messageType);
    }
  }
}
